import moment from 'moment/moment'

import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { API_BASE } from '../utils/constants'
import { BaseRecord } from './BaseRecord'
import { FKType } from './SQLBuilder'
import { User } from './User'

type ImageConstructorArgs = ConstructorArgs<() => Image>

export class Image extends BaseRecord {
    readonly recordType = 'image'

    uri?: string
    name?: string
    type?: string
    image?: JSX.Element
    size?: number // Integer
    expiredDate?: moment.Moment

    readonly typeString?: string
    readonly sizeString?: string
    readonly expireString?: string

    constructor(data?: Partial<ImageConstructorArgs>, params?: { id?: string; uri?: string }) {
        super(data)
        if (!data) {
            this.id = params?.id
            this.uri = params?.uri
            return
        }

        this.uri = data.uri ?? undefined
        this.type = data.type ?? undefined
        this.name = data.name ?? undefined
        this.size = this.propertyFromIntArg(data.size)
        this.expiredDate = this.propertyFromDateArg(data.expired_date)

        this.typeString = data.type_string ?? undefined
        this.sizeString = data.size_string ?? undefined
        this.expireString = data.expire_string ?? undefined
    }

    clone(overrides?: Partial<ImageConstructorArgs>): Image {
        return new Image({ ...this.serializeForClone(), ...overrides })
    }

    static initWithId(id: string, uri: string) {
        return new Image(undefined, { id, uri })
    }

    static initWithUri(uri: string) {
        return new Image(undefined, { uri })
    }

    get isImage(): boolean {
        return !this.type || !!this.image || this.type.startsWith('image/')
    }

    get url(): string | undefined {
        if (this.uri && this.uri.indexOf('//') == 0) {
            return `http:${this.uri}`
        }
        if (this.uri && this.uri.indexOf('/') == 0) {
            return `${API_BASE}${this.uri}`
        }
        return this.uri
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.expiredDate) this.expiredDate = moment(data.expiredDate)
    }

    static fk: FKType = {
        users: ['owner'],
    }

    static dataType(): DataType {
        return 'images'
    }

    get owner(): User | undefined {
        const owner = this.foreignKeyData['owner']
        if (typeof owner === 'string') {
            return User.initWithId(owner)
        }
        else if (typeof owner === 'object') {
            return new User(owner)
        }
        else {
            return undefined
        }
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
}

// Just like in models/Line, this gets around a circular dependency issue
// I (Daniel) couldn't figure out.
;(window as any).PSImageModel = Image
